import { Controller } from '@hotwired/stimulus'

// Mobile menu drawer. Locks page scroll with position:fixed on body (safe for sticky headers).
// Menu panel stays in the header document flow so it always opens below the nav bar.
export default class extends Controller {
  static targets = ['panel', 'openIcon', 'closeIcon', 'toggle']

  connect () {
    this.scrollY = 0
    this.isOpen = false
    this.handleKeydown = (event) => {
      if (event.key === 'Escape') this.close()
    }
    this.handleTurboBeforeVisit = () => this.close()
    this.handlePanelLinkClick = (event) => {
      if (event.target.closest('a, button[type="submit"]')) this.close()
    }

    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('turbo:before-visit', this.handleTurboBeforeVisit)

    if (this.hasPanelTarget) {
      this.panelTarget.addEventListener('click', this.handlePanelLinkClick)
    }

    this.close()
  }

  disconnect () {
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('turbo:before-visit', this.handleTurboBeforeVisit)

    if (this.hasPanelTarget) {
      this.panelTarget.removeEventListener('click', this.handlePanelLinkClick)
    }

    this.close()
  }

  toggle () {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open () {
    if (this.isOpen || !this.hasPanelTarget) return

    this.isOpen = true
    document.documentElement.classList.add('mobile-nav-open')
    this.lockBodyScroll()
    this.panelTarget.classList.remove('hidden')

    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute('aria-expanded', 'true')
    }
    if (this.hasOpenIconTarget) {
      this.openIconTarget.classList.add('hidden')
    }
    if (this.hasCloseIconTarget) {
      this.closeIconTarget.classList.remove('hidden')
    }
  }

  close () {
    if (!this.hasPanelTarget) return

    this.isOpen = false
    document.documentElement.classList.remove('mobile-nav-open')
    this.panelTarget.classList.add('hidden')

    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute('aria-expanded', 'false')
    }
    if (this.hasOpenIconTarget) {
      this.openIconTarget.classList.remove('hidden')
    }
    if (this.hasCloseIconTarget) {
      this.closeIconTarget.classList.add('hidden')
    }

    this.unlockBodyScroll()
  }

  lockBodyScroll () {
    if (document.body.dataset.mobileNavScrollLock === 'true') return

    this.scrollY = window.scrollY || document.documentElement.scrollTop || 0
    document.body.dataset.mobileNavScrollLock = 'true'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
  }

  unlockBodyScroll () {
    if (document.body.dataset.mobileNavScrollLock !== 'true') return

    const scrollY = this.scrollY || 0
    delete document.body.dataset.mobileNavScrollLock
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.width = ''
    window.scrollTo(0, scrollY)
  }
}
